import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { openAccountModal } from '@/features/user/accountModalSlice';
import { guestNudge, rewardFromLabel } from '../format';
import { useGuestProgram, useLoyaltyApiPresent } from '../useGuestLoyalty';

interface Props {
  restaurantUuid: string;
  /** The goods subtotal, in cents: the drawer does not know delivery or options yet. */
  subtotalCents: number;
}

function Line({ restaurantUuid, subtotalCents }: Props) {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { pathname, search } = useLocation();
  const channel = useAppSelector((s) => s.checkout?.payment?.channel ?? 'online');
  const program = useGuestProgram(restaurantUuid);
  const nudge = guestNudge(program, subtotalCents, channel);
  if (!nudge) return null;

  // Back to the very page (and the open cart) the customer left: the account modal
  // navigates to `redirectTo` when it is done, the cart and the checkout fields live
  // in the store and survive it.
  const redirectTo = `${pathname}${search}`;
  const counter = nudge.kind === 'counter';
  return (
    <div
      data-testid="cart-guest-nudge"
      className="loyalty-scope flex items-center gap-2 rounded-btn-sm border-2 border-solid border-ink bg-cream px-3 py-2 font-mono text-xs text-ink"
    >
      <span className="min-w-0 flex-1">
        <span aria-hidden="true">🎁 </span>
        {counter ? t('loyalty.guest.cartCounter') : t('loyalty.guest.cart', { n: nudge.points })}
        {/* SC-1640 — « ~12 pts » said nothing of what points buy: the cheapest
            reward does. Never on pay on site, which promises no points. */}
        {nudge.kind === 'points' &&
          nudge.reward &&
          ` · ${rewardFromLabel(t, nudge.reward, i18n.language)}`}
      </span>
      <button
        type="button"
        onClick={() => dispatch(openAccountModal({ mode: counter ? 'register' : 'login', redirectTo }))}
        data-testid="cart-guest-nudge-action"
        className="min-h-[44px] flex-none rounded-btn-sm border-2 border-solid border-ink bg-mint px-3 font-display text-sm text-[var(--on-primary)]"
      >
        {counter ? t('loyalty.guest.register') : t('loyalty.guest.login')}
      </button>
    </div>
  );
}

/**
 * SC-1091 — in the cart drawer, a guest is told what this order would earn if they
 * logged in. Behind the API gate: cart-only stores have no loyalty reducer.
 */
export default function CartGuestNudge(props: Props) {
  if (!useLoyaltyApiPresent()) return null;
  return <Line {...props} />;
}
